// ════════════════════════════════════════════════════════════════
// Access Control — Central Engine
// Single entry point: evaluate()
// 4-stage pipeline: System → Feature → Content → Action
// Pure functions, no React, usable in Edge Functions.
// ════════════════════════════════════════════════════════════════

import type {
  AccessDecision,
  AccessRequest,
  AccessTier,
  ActionKey,
  ContentRef,
  FeatureKey,
  UserContext,
  PreviewContext,
  DbFeatureRule,
  DbContentRule,
  DbRulesContext,
  SystemAccessConfig,
  TraceStep,
  ReasonCode,
  GateType,
  FeatureRule,
  ActionRule,
  ContentRule,
} from './types';
import { FEATURE_RULES, ACTION_RULES, CONTENT_RULES } from './rules';

// ══════════════════════════════════════════════════════════════
// INTERNAL HELPERS
// ══════════════════════════════════════════════════════════════

const TIER_RANK: Record<AccessTier, number> = { guest: 0, free: 1, premium: 2 };

function meetsMinTier(userTier: AccessTier, required: AccessTier): boolean {
  return TIER_RANK[userTier] >= TIER_RANK[required];
}

function userHasRole(ctx: UserContext, roles?: string[]): boolean {
  if (!roles || roles.length === 0) return false;
  return ctx.roles.some(r => roles.includes(r));
}

function userHasEntitlement(ctx: UserContext, code?: string): boolean {
  if (!code) return false;
  return ctx.entitlementCodes.includes(code);
}

function userHasBeta(ctx: UserContext, betaProgramIds?: string[]): boolean {
  if (!betaProgramIds || betaProgramIds.length === 0) return false;
  return ctx.betaProgramIds?.some(id => betaProgramIds.includes(id)) ?? false;
}

/**
 * AND-Semantik: User muss Mitglied in ALLEN aufgefuehrten Programmen sein.
 * Leeres Array bedeutet "keine zusaetzliche AND-Anforderung" (= pass).
 */
function userHasAllBeta(ctx: UserContext, requiredIds?: string[]): boolean {
  if (!requiredIds || requiredIds.length === 0) return true;
  const memberships = ctx.betaProgramIds ?? [];
  return requiredIds.every(id => memberships.includes(id));
}

function gateTypeForReason(reason: ReasonCode): GateType {
  switch (reason) {
    case 'granted':
    case 'super_admin_override':
    case 'preview_mode':
    case 'owner_override':
    case 'entitlement_override':
      return 'none';
    case 'not_authenticated':
      return 'auth';
    case 'premium_required':
      return 'premium';
    case 'feature_disabled':
    case 'system_maintenance':
      return 'disabled';
    default:
      return 'role';
  }
}

function grant(reason: ReasonCode = 'granted'): AccessDecision {
  return { allowed: true, reason, gateType: 'none' };
}

function deny(reason: ReasonCode): AccessDecision {
  return { allowed: false, reason, gateType: gateTypeForReason(reason) };
}

// ══════════════════════════════════════════════════════════════
// STAGE 1: SYSTEM CHECK
// ══════════════════════════════════════════════════════════════

function checkSystem(
  ctx: UserContext,
  config?: SystemAccessConfig,
  trace?: TraceStep[],
): AccessDecision | null {
  // Maintenance mode
  if (config?.maintenanceMode) {
    trace?.push({ stage: 'system', rule: 'maintenance_mode', result: ctx.isSuperAdmin ? 'pass' : 'deny' });
    if (!ctx.isSuperAdmin) return deny('system_maintenance');
  }

  // Account status check — suspended/rejected accounts are blocked
  if (ctx.accountStatus && ctx.accountStatus !== 'active') {
    if (ctx.accountStatus === 'suspended') {
      trace?.push({ stage: 'system', rule: 'account_status', result: 'deny', detail: 'account suspended' });
      return deny('account_suspended');
    }
    // invited, registered, rejected — not yet active
    trace?.push({ stage: 'system', rule: 'account_status', result: 'deny', detail: `status: ${ctx.accountStatus}` });
    return deny('account_not_active');
  }

  trace?.push({ stage: 'system', rule: 'system_ok', result: 'pass' });
  return null; // continue to next stage
}

// ══════════════════════════════════════════════════════════════
// STAGE 2: FEATURE ACCESS
// ══════════════════════════════════════════════════════════════

function resolveDbFeature(ctx: UserContext, dbRule: DbFeatureRule, trace?: TraceStep[]): AccessDecision {
  const ruleDesc = `db:feature_rules[${dbRule.feature_key}]`;

  if (!dbRule.is_enabled || dbRule.access_mode === 'disabled') {
    trace?.push({ stage: 'feature', rule: ruleDesc, result: 'deny', detail: 'feature disabled' });
    return deny('feature_disabled');
  }

  // Verification check
  if (dbRule.requires_verification && dbRule.min_verification) {
    const VERIFICATION_RANK: Record<string, number> = {
      unverified: 0, email_verified: 1, identity_verified: 2, community_verified: 3,
    };
    const userRank = VERIFICATION_RANK[ctx.verificationStatus ?? 'unverified'] ?? 0;
    const requiredRank = VERIFICATION_RANK[dbRule.min_verification] ?? 0;
    if (userRank < requiredRank) {
      trace?.push({ stage: 'feature', rule: ruleDesc, result: 'deny', detail: `requires ${dbRule.min_verification}, user has ${ctx.verificationStatus ?? 'unverified'}` });
      return deny('verification_required');
    }
  }

  if (dbRule.access_mode === 'open' && !dbRule.requires_auth) {
    trace?.push({ stage: 'feature', rule: ruleDesc, result: 'pass', detail: 'open access' });
    return grant();
  }

  if ((dbRule.access_mode !== 'open' || dbRule.requires_auth) && !ctx.userId) {
    trace?.push({ stage: 'feature', rule: ruleDesc, result: 'deny', detail: 'auth required' });
    return deny('not_authenticated');
  }

  if (dbRule.access_mode === 'beta_only') {
    // AND-Anforderung (required_beta_program_ids) zuerst pruefen
    if (!userHasAllBeta(ctx, dbRule.required_beta_program_ids)) {
      trace?.push({ stage: 'feature', rule: ruleDesc, result: 'deny', detail: 'required beta programs missing (AND)' });
      return deny('beta_program_required');
    }
    // Wenn nur required gesetzt ist und keine OR-Liste, AND erfüllt = pass
    if ((!dbRule.allowed_beta_program_ids || dbRule.allowed_beta_program_ids.length === 0)
        && (dbRule.required_beta_program_ids?.length ?? 0) > 0) {
      trace?.push({ stage: 'feature', rule: ruleDesc, result: 'pass', detail: 'all required beta programs present (AND)' });
      return grant();
    }
    if (userHasBeta(ctx, dbRule.allowed_beta_program_ids)) {
      trace?.push({ stage: 'feature', rule: ruleDesc, result: 'pass', detail: 'beta access' });
      return grant();
    }
    trace?.push({ stage: 'feature', rule: ruleDesc, result: 'deny', detail: 'beta program required' });
    return deny('beta_program_required');
  }

  if (dbRule.access_mode === 'role_restricted') {
    if (userHasRole(ctx, dbRule.allowed_roles)) {
      trace?.push({ stage: 'feature', rule: ruleDesc, result: 'pass', detail: `role match: ${ctx.roles.filter(r => dbRule.allowed_roles.includes(r)).join(',')}` });
      return grant();
    }
    if (!userHasAllBeta(ctx, dbRule.required_beta_program_ids)) {
      trace?.push({ stage: 'feature', rule: ruleDesc, result: 'deny', detail: 'required beta programs missing (AND)' });
      return deny('beta_program_required');
    }
    if (userHasBeta(ctx, dbRule.allowed_beta_program_ids)) {
      trace?.push({ stage: 'feature', rule: ruleDesc, result: 'pass', detail: 'beta program override' });
      return grant();
    }
    trace?.push({ stage: 'feature', rule: ruleDesc, result: 'deny', detail: `requires roles: ${dbRule.allowed_roles.join(',')}` });
    return deny('role_not_allowed');
  }

  // auth_required mode — any authenticated user
  trace?.push({ stage: 'feature', rule: ruleDesc, result: 'pass', detail: 'authenticated' });
  return grant();
}

function resolveStaticFeature(ctx: UserContext, feature: FeatureKey, trace?: TraceStep[]): AccessDecision {
  const rule: FeatureRule | undefined = FEATURE_RULES[feature];
  const ruleDesc = `static:FEATURE_RULES[${feature}]`;

  if (!rule) {
    trace?.push({ stage: 'feature', rule: ruleDesc, result: 'pass', detail: 'no rule defined' });
    return grant();
  }

  if (rule.betaOnly && !userHasEntitlement(ctx, 'can_access_beta_features') && !userHasRole(ctx, ['admin', 'super_admin'])) {
    trace?.push({ stage: 'feature', rule: ruleDesc, result: 'deny', detail: 'beta only' });
    return deny('beta_program_required');
  }

  if (userHasEntitlement(ctx, rule.entitlementOverride)) {
    trace?.push({ stage: 'feature', rule: ruleDesc, result: 'pass', detail: `entitlement: ${rule.entitlementOverride}` });
    return grant('entitlement_override');
  }

  if (userHasRole(ctx, rule.allowedRoles)) {
    trace?.push({ stage: 'feature', rule: ruleDesc, result: 'pass', detail: 'role match' });
    return grant();
  }

  if (meetsMinTier(ctx.tier, rule.minTier)) {
    trace?.push({ stage: 'feature', rule: ruleDesc, result: 'pass', detail: `tier ${ctx.tier} >= ${rule.minTier}` });
    return grant();
  }

  if (ctx.tier === 'guest') {
    trace?.push({ stage: 'feature', rule: ruleDesc, result: 'deny', detail: 'guest, auth required' });
    return deny('not_authenticated');
  }

  if (TIER_RANK[rule.minTier] > TIER_RANK[ctx.tier]) {
    trace?.push({ stage: 'feature', rule: ruleDesc, result: 'deny', detail: `tier ${ctx.tier} < ${rule.minTier}` });
    return deny('premium_required');
  }

  trace?.push({ stage: 'feature', rule: ruleDesc, result: 'deny', detail: 'role not allowed' });
  return deny('role_not_allowed');
}

function checkFeature(
  ctx: UserContext,
  feature: FeatureKey,
  dbCtx?: DbRulesContext,
  config?: SystemAccessConfig,
  trace?: TraceStep[],
): AccessDecision {
  // System-level kill switch
  if (config?.disabledFeatures?.includes(feature)) {
    trace?.push({ stage: 'feature', rule: 'system:disabledFeatures', result: 'deny', detail: feature });
    return deny('feature_disabled');
  }

  // DB rule has priority
  const dbRule = dbCtx?.featureRules?.get(feature);
  if (dbRule) return resolveDbFeature(ctx, dbRule, trace);

  // Static fallback
  return resolveStaticFeature(ctx, feature, trace);
}

// ══════════════════════════════════════════════════════════════
// STAGE 3: CONTENT ACCESS
// ══════════════════════════════════════════════════════════════

function checkContent(
  ctx: UserContext,
  content: ContentRef,
  dbCtx?: DbRulesContext,
  config?: SystemAccessConfig,
  trace?: TraceStep[],
): AccessDecision {
  // Resolve parent feature from static rules
  const staticRule: ContentRule | undefined = CONTENT_RULES[content.type];
  const parentFeature = staticRule?.feature as FeatureKey | undefined;

  // Feature gate first
  if (parentFeature) {
    const featureResult = checkFeature(ctx, parentFeature, dbCtx, config, trace);
    if (!featureResult.allowed) {
      trace?.push({ stage: 'content', rule: `feature_gate:${parentFeature}`, result: 'deny', detail: featureResult.reason });
      return { ...featureResult, reason: 'content_inherited_deny' };
    }
  }

  // DB content rule: exact match → type default
  if (dbCtx?.contentRules) {
    const exactRule = content.id
      ? dbCtx.contentRules.find(r => r.content_type === content.type && r.content_id === content.id)
      : undefined;
    const typeRule = dbCtx.contentRules.find(r => r.content_type === content.type && r.content_id === null);
    const dbContentRule = exactRule ?? typeRule;

    if (dbContentRule) {
      const ruleDesc = `db:content_rules[${dbContentRule.content_type}/${dbContentRule.content_id ?? '*'}]`;

      // Inherit from feature
      if (dbContentRule.inherit_from_feature) {
        const dbFeatureRule = dbCtx.featureRules?.get(dbContentRule.feature_key);
        if (dbFeatureRule) {
          trace?.push({ stage: 'content', rule: ruleDesc, result: 'skip', detail: 'inheriting from feature' });
          return resolveDbFeature(ctx, dbFeatureRule, trace);
        }
      }

      // Own access mode
      if (dbContentRule.access_mode === 'disabled') {
        trace?.push({ stage: 'content', rule: ruleDesc, result: 'deny', detail: 'content disabled' });
        return deny('content_restricted');
      }

      if (dbContentRule.requires_verification && dbContentRule.min_verification) {
        const VRANK: Record<string, number> = { unverified: 0, email_verified: 1, identity_verified: 2, community_verified: 3 };
        if ((VRANK[ctx.verificationStatus ?? 'unverified'] ?? 0) < (VRANK[dbContentRule.min_verification] ?? 0)) {
          trace?.push({ stage: 'content', rule: ruleDesc, result: 'deny', detail: 'verification required' });
          return deny('verification_required');
        }
      }

      if (dbContentRule.access_mode === 'open' && !dbContentRule.requires_auth) {
        trace?.push({ stage: 'content', rule: ruleDesc, result: 'pass' });
        return grant();
      }

      if ((dbContentRule.access_mode !== 'open' || dbContentRule.requires_auth) && !ctx.userId) {
        trace?.push({ stage: 'content', rule: ruleDesc, result: 'deny', detail: 'auth required' });
        return deny('not_authenticated');
      }

      if (dbContentRule.access_mode === 'beta_only') {
        if (userHasBeta(ctx, dbContentRule.allowed_beta_program_ids)) {
          trace?.push({ stage: 'content', rule: ruleDesc, result: 'pass', detail: 'beta' });
          return grant();
        }
        trace?.push({ stage: 'content', rule: ruleDesc, result: 'deny', detail: 'beta required' });
        return deny('beta_program_required');
      }

      if (dbContentRule.access_mode === 'role_restricted') {
        if (userHasRole(ctx, dbContentRule.allowed_roles) || userHasBeta(ctx, dbContentRule.allowed_beta_program_ids)) {
          trace?.push({ stage: 'content', rule: ruleDesc, result: 'pass' });
          return grant();
        }
        trace?.push({ stage: 'content', rule: ruleDesc, result: 'deny', detail: 'role restricted' });
        return deny('role_not_allowed');
      }

      trace?.push({ stage: 'content', rule: ruleDesc, result: 'pass' });
      return grant();
    }
  }

  // Static content rule fallback
  if (!staticRule) {
    trace?.push({ stage: 'content', rule: `static:CONTENT_RULES[${content.type}]`, result: 'pass', detail: 'no rule' });
    return grant();
  }

  // Owner override
  if (staticRule.ownerAllowed && content.ownerId && ctx.userId === content.ownerId) {
    trace?.push({ stage: 'content', rule: `static:CONTENT_RULES[${content.type}]`, result: 'pass', detail: 'owner' });
    return grant('owner_override');
  }

  if (userHasEntitlement(ctx, staticRule.entitlementOverride)) {
    trace?.push({ stage: 'content', rule: `static:CONTENT_RULES[${content.type}]`, result: 'pass', detail: 'entitlement' });
    return grant('entitlement_override');
  }

  if (userHasRole(ctx, staticRule.allowedRoles) || meetsMinTier(ctx.tier, staticRule.minTier)) {
    trace?.push({ stage: 'content', rule: `static:CONTENT_RULES[${content.type}]`, result: 'pass' });
    return grant();
  }

  if (ctx.tier === 'guest') {
    trace?.push({ stage: 'content', rule: `static:CONTENT_RULES[${content.type}]`, result: 'deny' });
    return deny('not_authenticated');
  }

  trace?.push({ stage: 'content', rule: `static:CONTENT_RULES[${content.type}]`, result: 'deny' });
  return deny('content_restricted');
}

// ══════════════════════════════════════════════════════════════
// STAGE 4: ACTION ACCESS
// ══════════════════════════════════════════════════════════════

function checkAction(
  ctx: UserContext,
  action: ActionKey,
  contentOwnerId?: string,
  dbCtx?: DbRulesContext,
  config?: SystemAccessConfig,
  trace?: TraceStep[],
): AccessDecision {
  const rule: ActionRule | undefined = ACTION_RULES[action];
  const ruleDesc = `static:ACTION_RULES[${action}]`;

  if (!rule) {
    trace?.push({ stage: 'action', rule: ruleDesc, result: 'pass', detail: 'no rule' });
    return grant();
  }

  // Parent feature must pass
  const featureResult = checkFeature(ctx, rule.feature, dbCtx, config, trace);
  if (!featureResult.allowed) {
    trace?.push({ stage: 'action', rule: ruleDesc, result: 'deny', detail: `parent feature ${rule.feature} denied` });
    return { ...featureResult, reason: featureResult.reason === 'feature_disabled' ? 'feature_disabled' : 'feature_not_allowed' };
  }

  // Owner override
  if (rule.ownerAllowed && contentOwnerId && ctx.userId === contentOwnerId) {
    trace?.push({ stage: 'action', rule: ruleDesc, result: 'pass', detail: 'owner override' });
    return grant('owner_override');
  }

  if (userHasEntitlement(ctx, rule.entitlementOverride)) {
    trace?.push({ stage: 'action', rule: ruleDesc, result: 'pass', detail: `entitlement: ${rule.entitlementOverride}` });
    return grant('entitlement_override');
  }

  if (userHasRole(ctx, rule.allowedRoles)) {
    trace?.push({ stage: 'action', rule: ruleDesc, result: 'pass', detail: 'role match' });
    return grant();
  }

  if (meetsMinTier(ctx.tier, rule.minTier)) {
    trace?.push({ stage: 'action', rule: ruleDesc, result: 'pass', detail: `tier ${ctx.tier} >= ${rule.minTier}` });
    return grant();
  }

  if (ctx.tier === 'guest') {
    trace?.push({ stage: 'action', rule: ruleDesc, result: 'deny', detail: 'not authenticated' });
    return deny('not_authenticated');
  }

  if (TIER_RANK[rule.minTier] > TIER_RANK[ctx.tier]) {
    trace?.push({ stage: 'action', rule: ruleDesc, result: 'deny', detail: `tier ${ctx.tier} < ${rule.minTier}` });
    return deny('premium_required');
  }

  trace?.push({ stage: 'action', rule: ruleDesc, result: 'deny', detail: 'action not allowed' });
  return deny('action_not_allowed');
}

// ══════════════════════════════════════════════════════════════
// PUBLIC API — SINGLE ENTRY POINT
// ══════════════════════════════════════════════════════════════

/**
 * Central access evaluation function.
 *
 * 4-stage pipeline (stops on first deny):
 *   1. System check (maintenance mode, kill switches)
 *   2. Feature access (is the module enabled for this user?)
 *   3. Content access (can user see this specific content?)
 *   4. Action access (can user perform this action?)
 *
 * Provide only the stages you need:
 *   - evaluate({ user, feature: 'admin' })
 *   - evaluate({ user, content: { type: 'spot', id: '123' } })
 *   - evaluate({ user, action: 'spot.edit', contentOwnerId: '...' })
 *   - evaluate({ user, feature: 'admin', action: 'admin.access', debug: true })
 */
export function evaluate(
  request: AccessRequest,
  dbCtx?: DbRulesContext,
  config?: SystemAccessConfig,
): AccessDecision {
  const trace: TraceStep[] = [];
  const debug = request.debug ?? false;
  const t = debug ? trace : undefined;

  // ── Preview mode override ────────────────────────────────
  let ctx = request.user;
  const isPreview = !!request.preview && !request.preview.bypassAll;
  if (request.preview) {
    if (request.preview.bypassAll) {
      t?.push({ stage: 'system', rule: 'preview:bypassAll', result: 'pass' });
      return { allowed: true, reason: 'preview_mode', gateType: 'none', trace: debug ? trace : undefined };
    }
    // Apply preview overrides to a copy of the context — disable super_admin so
    // the simulated role/tier is actually evaluated through the full pipeline.
    ctx = {
      ...ctx,
      tier: request.preview.asTier ?? ctx.tier,
      roles: request.preview.asRoles ?? ctx.roles,
      entitlementCodes: request.preview.asEntitlements ?? ctx.entitlementCodes,
      betaProgramIds: request.preview.asBetaProgramIds ?? ctx.betaProgramIds,
      verificationStatus: request.preview.asVerificationStatus ?? ctx.verificationStatus,
      userId: request.preview.asUserId ?? ctx.userId,
      isSuperAdmin: false, // always disable in preview so the pipeline runs fully
    };
    t?.push({ stage: 'system', rule: 'preview:context_override', result: 'pass', detail: `tier=${ctx.tier}, roles=${ctx.roles.join(',')}, verification=${ctx.verificationStatus}` });
  }

  // ── Super admin bypass ───────────────────────────────────
  if (ctx.isSuperAdmin) {
    t?.push({ stage: 'system', rule: 'super_admin', result: 'pass' });
    return { allowed: true, reason: 'super_admin_override', gateType: 'none', trace: debug ? trace : undefined };
  }

  // ── Stage 1: System ──────────────────────────────────────
  const systemResult = checkSystem(ctx, config, t);
  if (systemResult) return { ...systemResult, trace: debug ? trace : undefined };

  // ── Stage 2: Feature ─────────────────────────────────────
  if (request.feature) {
    const featureResult = checkFeature(ctx, request.feature, dbCtx, config, t);
    if (!featureResult.allowed) return { ...featureResult, trace: debug ? trace : undefined };
  }

  // ── Stage 3: Content ─────────────────────────────────────
  if (request.content) {
    const contentResult = checkContent(ctx, request.content, dbCtx, config, t);
    if (!contentResult.allowed) return { ...contentResult, trace: debug ? trace : undefined };
  }

  // ── Stage 4: Action ──────────────────────────────────────
  if (request.action) {
    const actionResult = checkAction(ctx, request.action, request.contentOwnerId, dbCtx, config, t);
    if (!actionResult.allowed) return { ...actionResult, trace: debug ? trace : undefined };
  }

  return { allowed: true, reason: 'granted', gateType: 'none', trace: debug ? trace : undefined };
}

// ══════════════════════════════════════════════════════════════
// CONVENIENCE WRAPPERS (for backward compat + simpler usage)
// ══════════════════════════════════════════════════════════════

export function checkFeatureAccess(ctx: UserContext, feature: FeatureKey, dbCtx?: DbRulesContext): AccessDecision {
  return evaluate({ user: ctx, feature }, dbCtx);
}

export function checkContentAccess(ctx: UserContext, content: ContentRef, dbCtx?: DbRulesContext): AccessDecision {
  return evaluate({ user: ctx, content }, dbCtx);
}

export function checkActionAccess(ctx: UserContext, action: ActionKey, contentOwnerId?: string, dbCtx?: DbRulesContext): AccessDecision {
  return evaluate({ user: ctx, action, contentOwnerId }, dbCtx);
}

// ── Build UserContext ────────────────────────────────────────
export function buildUserContext(
  userId: string | null,
  roles: string[],
  entitlementCodes: string[],
  isFreePlan: boolean,
  betaProgramIds?: string[],
  verificationStatus?: string,
  accountStatus?: string,
): UserContext {
  const isSuperAdmin = roles.includes('admin') || roles.includes('super_admin');
  const tier: AccessTier = !userId ? 'guest' : isFreePlan ? 'free' : 'premium';
  return { userId, tier, roles, entitlementCodes, isSuperAdmin, betaProgramIds, verificationStatus, accountStatus };
}
